import { ContactUpdateFields } from '@components/ContactDetailsForm';
import { t } from '@lingui/core/macro';
import type { UserGroupDataQualityField, UserGroupDataQualityIssueRecord } from '@navio/nsdk';
import { AddressLabel } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { FieldErrorMessage, NoddiDialog, NoddiSheet, useIsMobile } from 'noddi-ui-web';
import { useState } from 'react';
import { ProfileAddressForm } from '../Booking/ProfileAddressForm';
import { NameUpdateFields } from './NameUpdateFields';
import {
  getDataQualityFixStepCtaText,
  getDataQualityFixStepDescription,
  getDataQualityFixSteps,
  getDataQualityFixStepTitle,
  type CustomerDataQualityFixStep
} from './customerDataQualityFixes';

type Props = {
  open: boolean;
  onClose: () => void;
  userGroupId: number | null;
  countryCode?: string;
  /** Whether finishing the last step starts a payment rather than only creating the booking. */
  willPay: boolean;
  /** Names pay or confirm on the last step. A banner save leaves the label as Save. */
  resumesCheckout: boolean;
  /** Runs once every detail is saved — continue the interrupted click, or just close. */
  onSaved: () => void;
  /** Steps are the data-quality issues the customer can fix, missing and invalid. */
  dataQualityIssues: readonly UserGroupDataQualityIssueRecord[];
  /**
   * A saved step is accepted only after the data-quality report drops that field.
   * Returning a message keeps the sheet on the step.
   */
  onRecheckStep: (field: UserGroupDataQualityField) => Promise<string | null>;
};

/**
 * Collects the booking details the data-quality report still rejects.
 * Saved on the account, not per booking.
 *
 * One detail per step, each saving on its own, so a failure on the second step
 * leaves the first already banked instead of asking for both again.
 *
 * Bottom sheet on a phone (thumb reach, per the design handover), plain centred
 * dialog from md up, where a full-width bar pinned to the bottom edge reads as
 * a cookie banner rather than a step in the checkout.
 */
export const DataQualityFixDialog = ({
  open,
  onClose,
  userGroupId,
  countryCode,
  willPay,
  resumesCheckout,
  onSaved,
  dataQualityIssues,
  onRecheckStep
}: Props) => {
  const isMobile = useIsMobile();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  // Frozen while open: a refetch that clears the issue would otherwise drop
  // the step the customer is standing on mid-flow.
  const [openSteps, setOpenSteps] = useState<CustomerDataQualityFixStep[] | null>(null);

  if (open && openSteps === null) {
    setOpenSteps(getDataQualityFixSteps(dataQualityIssues));
    setStepIndex(0);
    setValidationError(null);
  }
  if (!open && openSteps !== null) {
    setOpenSteps(null);
  }

  const steps = openSteps ?? [];
  const current = steps[stepIndex];
  const step = current?.step;
  const isMultiStep = steps.length > 1;
  const isLastStep = stepIndex === steps.length - 1;

  async function goToNextStep() {
    if (current) {
      try {
        const message = await onRecheckStep(current.field);
        if (message) {
          setValidationError(message);
          return;
        }
      } catch {
        setValidationError(t`Could not check booking details`);
        return;
      }
    }
    setValidationError(null);
    if (isLastStep) {
      onSaved();
      return;
    }
    setStepIndex(stepIndex + 1);
  }

  if (!step) {
    return null;
  }

  const ctaLabel = getDataQualityFixStepCtaText({ step, isLastStep: isLastStep && resumesCheckout, willPay });
  const ctaClassName = cn('w-full', isMobile && 'h-12');
  const title = getDataQualityFixStepTitle(step, isMultiStep);
  const description = getDataQualityFixStepDescription(step, isMultiStep);

  const body = (
    <div className={cn('flex flex-col px-0.5', isMobile ? 'gap-5 pt-1' : 'gap-4')}>
      {isMultiStep && <StepProgress total={steps.length} currentIndex={stepIndex} />}
      {validationError && <FieldErrorMessage errorMessage={validationError} />}

      {step === 'email' && (
        <ContactUpdateFields
          field='email'
          onSaved={() => {
            void goToNextStep();
          }}
          saveLabel={ctaLabel}
        />
      )}

      {step === 'name' && (
        <NameUpdateFields
          onSaved={() => {
            void goToNextStep();
          }}
          saveLabel={ctaLabel}
        />
      )}

      {step === 'phone' && (
        <ContactUpdateFields
          field='phoneNumber'
          onSaved={() => {
            void goToNextStep();
          }}
          saveLabel={ctaLabel}
        />
      )}

      {step === 'address' &&
        (userGroupId != null ? (
          <ProfileAddressForm
            userGroupId={userGroupId}
            // The report counts an invoice address only when the link carries
            // this label, so any other label prevents the step from being completed.
            label={AddressLabel.INVOICE}
            countryCode={countryCode}
            ctaLabel={ctaLabel}
            ctaVariant='success'
            ctaClassName={ctaClassName}
            gapClassName={isMobile ? 'gap-6' : 'gap-4'}
            showApiErrorInline
            inlineSearch={isMobile}
            onSaved={() => {
              void goToNextStep();
            }}
          />
        ) : (
          <FieldErrorMessage errorMessage={t`Please add an address to your profile before confirming`} />
        ))}
    </div>
  );

  if (isMobile) {
    return (
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        side='bottom'
        // A bottom sheet with an auto height grows upward, so the suggestion list opening under
        // the address field shoved the field off the top of the screen. Pin the height on that
        // step and let the body scroll instead — nothing above the list can move.
        className={cn('rounded-t-2xl', step === 'address' ? 'h-[85vh]' : 'max-h-[85vh]')}
        closeText={t`Close`}
        title={<span className='text-lg leading-tight font-semibold'>{title}</span>}
        description={description}
      >
        {body}
      </NoddiSheet>
    );
  }

  return (
    <NoddiDialog open={open} onClose={onClose} title={title} description={description} maxWidth='sm'>
      {body}
    </NoddiDialog>
  );
};

/** Slim segmented progress for the two-detail case; one detail shows no stepper at all. */
const StepProgress = ({ total, currentIndex }: { total: number; currentIndex: number }) => {
  const step = currentIndex + 1;

  return (
    <div className='flex items-center gap-3'>
      <div className='flex flex-1 gap-1' aria-hidden>
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={cn('h-1 flex-1 rounded-full', index <= currentIndex ? 'bg-darkPurple' : 'bg-darkPurple40')}
          />
        ))}
      </div>
      <span className='text-xs text-muted-foreground tabular-nums'>{t`Step ${step} of ${total}`}</span>
    </div>
  );
};
